// apps/merchant-v3/src/lib/notificationsI18n.ts
//
// Re-render a notification row's title/body at the ACTIVE i18next locale
// via @printway/i18n's lookup() — mirrors the vendor app helper
// (apps/vendor/src/features/notifications/api.ts, Amendment #1 UAT B2).
//
// Why: the BE persists title/body rendered at dispatch time (recipient's
// stored User.language). When the merchant switches locale in the topbar,
// stored strings would stay frozen — this helper re-renders rows that
// carry an i18n_key so the feed follows the UI locale
// (dbg-20260704-110206-3372).
//
// Fallback chain: no key → stored strings; unknown key → stored strings;
// lookup throws (missing translation) → stored strings.

import {
  isNotificationKey,
  lookup,
  type NotificationLocale,
} from '@printway/i18n';

import type { Notification } from '@/types/notification';

const SUPPORTED: readonly NotificationLocale[] = ['en', 'vi', 'fr'];

function normalizeLocale(lang: string | undefined): NotificationLocale {
  if (!lang) return 'en';
  const head = lang.toLowerCase().split('-')[0] ?? '';
  return (SUPPORTED as readonly string[]).includes(head)
    ? (head as NotificationLocale)
    : 'en';
}

export function translateNotification(
  row: Pick<Notification, 'title' | 'body' | 'i18nKey' | 'i18nParams'>,
  lang: string | undefined,
): { title: string; body: string } {
  const key = row.i18nKey;
  if (!key || !isNotificationKey(key)) {
    return { title: row.title, body: row.body };
  }
  try {
    const tpl = lookup(key, normalizeLocale(lang), row.i18nParams ?? undefined);
    return { title: tpl.title, body: tpl.body };
  } catch {
    return { title: row.title, body: row.body };
  }
}
